import React, { useState } from 'react';
import Style from './style.css';
import _round from 'lodash/round';
import TableTop from 'cccisd-tabletop';
import DatePicker from 'cccisd-datepicker';
import Tooltip from 'cccisd-tooltip';
import Modal from 'cccisd-modal';
import IconInfo from 'cccisd-icons/question4';
import messagesQuery from './emailMessages.graphql';
import clientEmailAppsQuery from './clientEmailApps.graphql';

const EmailMonitoring = () => {
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    const messagesTable = {
        orderBy: 'canonicalAt',
        isAscentOrder: false,
        query: messagesQuery,
        rowKey: 'transactionId',
        noRecordsMessage: 'No Email Messages found.',
        columns: [
            {
                name: 'app.label',
                label: 'Application',
                filter: true,
                sort: true,
            },
            {
                name: 'app.url',
                label: 'URL',
                filter: true,
                sort: true,
            },
            {
                name: 'questproject',
                label: 'Quest Project',
                filter: true,
                sort: true,
            },
            {
                name: 'status',
                label: 'Status',
                filter: true,
                sort: true,
                filterSettings: {
                    type: 'selectbox',
                    options: [
                        { label: 'Success', value: 'SUCCESS' },
                        { label: 'Failure', value: 'FAILURE' },
                        { label: 'Sent', value: 'sent' },
                    ],
                },
                render: ({ value }) => {
                    if (value === 'SUCCESS') {
                        return <span className="badge bg-success">Success</span>;
                    }
                    if (value === 'FAILURE') {
                        return <span className="badge bg-danger">Failure</span>;
                    }
                    if (value === 'sent') {
                        return <span className="badge bg-info">Sent</span>;
                    }
                    return <span className="badge bg-primary">{value}</span>;
                },
            },
            {
                name: 'message',
                label: 'Message',
                filter: true,
                sort: true,
            },
            {
                name: 'canonicalAt',
                label: 'Sent At',
                filter: true,
                sort: true,
                filterSettings: {
                    type: 'date',
                },
            },
            {
                name: 'transactionId',
                label: '',
                render: ({ row }) => {
                    let nameBlock =
                        row.firstName || row.lastName ? (
                            <div>
                                Name: {row.firstName} {row.lastName}
                            </div>
                        ) : (
                            ''
                        );

                    return (
                        <Modal
                            title="Additional Info"
                            trigger={
                                <Tooltip title="Additional Info">
                                    <button type="button" className="btn btn-xs btn-primary">
                                        <IconInfo />
                                    </button>
                                </Tooltip>
                            }
                        >
                            {nameBlock}
                            <br />
                            <div className={Style.ids}>
                                <em>{`Transaction ID: ${row.transactionId}`}</em>
                            </div>
                            <div className={Style.ids}>
                                <em>{`Delivery Status: ${row.status}`}</em>
                            </div>
                            <div className={Style.ids}>
                                <em>{`From: ${row.firstName} ${row.lastNight}, ${row.from}`}</em>
                            </div>
                            <div className={Style.ids}>
                                <em>{`To: ${row.firstName} ${row.lastNight}, ${row.to}`}</em>
                            </div>
                            <div className={Style.ids}>
                                <em>{`Status Details: ${row.statusDetails}`}</em>
                            </div>
                        </Modal>
                    );
                },
            },
        ],
    };

    const clientAppsTable = {
        query: clientEmailAppsQuery,
        rowKey: 'appId',
        orderBy: 'url',
        noRecordsMessage: 'No Client Apps found.',
        showTotal: true,
        graphqlVariables: {
            emailFilter: {
                range: {
                    field: 'cloudwatchAtDate',
                    fromString: from || '0000-01-01',
                    toString: to || '9999-12-31',
                },
            },
        },
        columns: [
            {
                name: 'label',
                label: 'Client App',
                filter: true,
                sort: true,
                render: ({ row, value }) => {
                    if (row.description) {
                        return (
                            <span>
                                {value}
                                <Tooltip title={row.description}>
                                    <IconInfo spaceLeft />
                                </Tooltip>
                            </span>
                        );
                    }
                    return value;
                },
            },
            {
                name: 'url',
                label: 'URL',
                filter: true,
                sort: true,
            },
            {
                name: 'environment',
                label: 'Environment',
                filter: true,
                sort: true,
            },
            {
                name: 'emailCount',
                label: 'Messages Sent',
                filter: true,
                sort: true,
                totalPath: 'appSummary.emailCountTotal',
            },
            {
                name: 'cost',
                label: 'Cost',
                filter: true,
                sort: true,
                totalPath: 'appSummary.totalCost',
                render: ({ value }) => {
                    return `$${_round(value, 2)}`;
                },
            },
        ],
    };

    const DateRangePicker = () => {
        return (
            <div style={{ display: 'flex', gap: '1em', alignItems: 'baseline' }}>
                <label className="form-label">From:</label>
                <DatePicker selected={from} onChange={v => setFrom(v)} placeholderText="Choose a date" />
                <label className="form-label">To:</label>
                <DatePicker selected={to} onChange={v => setTo(v)} placeholderText="Choose a date" />
                {(from || to) && (
                    <button
                        className="btn btn-sm"
                        type="button"
                        onClick={() => {
                            setFrom('');
                            setTo('');
                        }}
                    >
                        Clear Date Filter
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className={'row ' + Style.wrapper}>
            <h1>Email Monitoring</h1>
            <TableTop
                handle="emailMonitoring"
                saveState
                views={[
                    {
                        handle: 'messagesTable',
                        title: 'Email Messages',
                        table: messagesTable,
                    },
                    {
                        handle: 'clientAppTable',
                        title: 'Client Apps',
                        table: clientAppsTable,
                        buttons: [DateRangePicker],
                    },
                ]}
            />
        </div>
    );
};

export default EmailMonitoring;
